import React from 'react';
import { Minus, Square, Copy, X } from 'lucide-react';
import { windowControls } from '../utils/platform';
import { cn } from '../utils/cn';

interface WindowControlsProps {
  isMaximized: boolean;
  variant?: 'light' | 'dark';
  className?: string;
}

/**
 * Windows 11-style window control buttons (minimize, maximize/restore, close)
 *
 * Design:
 * - Flat, minimal style with subtle hover effects
 * - Close button turns red on hover (Windows 11 style)
 * - Shows restore icon when maximized, maximize icon when windowed
 * - Only visible on Windows platform (caller should check isWindows())
 */
export const WindowControls: React.FC<WindowControlsProps> = ({
  isMaximized,
  variant = 'dark',
  className,
}) => {
  const handleMinimize = () => {
    windowControls.minimize();
  };

  const handleMaximize = () => {
    windowControls.maximize();
  };

  const handleClose = () => {
    windowControls.close();
  };

  // Color classes based on variant
  const baseClasses = variant === 'dark'
    ? 'text-gray-400 hover:text-white'
    : 'text-gray-600 hover:text-gray-900';

  const hoverBgClasses = variant === 'dark'
    ? 'hover:bg-gray-800'
    : 'hover:bg-gray-100';

  const closeHoverClasses = variant === 'dark'
    ? 'hover:bg-red-600 hover:text-white'
    : 'hover:bg-red-500 hover:text-white';

  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {/* Minimize */}
      <button
        onClick={handleMinimize}
        className={cn(
          'w-7 h-7 flex items-center justify-center rounded transition-colors',
          baseClasses,
          hoverBgClasses
        )}
        title="Minimizar"
        aria-label="Minimize window"
      >
        <Minus className="h-3.5 w-3.5" strokeWidth={2} />
      </button>

      {/* Maximize/Restore */}
      <button
        onClick={handleMaximize}
        className={cn(
          'w-7 h-7 flex items-center justify-center rounded transition-colors',
          baseClasses,
          hoverBgClasses
        )}
        title={isMaximized ? 'Restaurar' : 'Maximizar'}
        aria-label={isMaximized ? 'Restore window' : 'Maximize window'}
      >
        {isMaximized ? (
          // Restore icon: two overlapping squares
          <Copy className="h-3.5 w-3.5" strokeWidth={2} />
        ) : (
          // Maximize icon: single square
          <Square className="h-3.5 w-3.5" strokeWidth={2} />
        )}
      </button>

      {/* Close */}
      <button
        onClick={handleClose}
        className={cn(
          'w-7 h-7 flex items-center justify-center rounded transition-colors',
          baseClasses,
          closeHoverClasses
        )}
        title="Fechar"
        aria-label="Close window"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
};
